/**
 * 한국어 사전. 이 파일이 **키의 원본**이다.
 *
 * `MessageKey`를 여기서 뽑아 `en.ts`가 `Record<MessageKey, string>`으로 받기 때문에,
 * 키를 하나 추가하면 영어가 비어 있을 때 컴파일이 먼저 막힌다. 런타임 테스트보다 이쪽이 싸다.
 *
 * ## 문구 규칙 (자세한 근거는 GLOSSARY.md)
 *
 * - 바뀐 지점은 언제나 **"이상한 곳"**이다. '변화'·'차이'·'정답'으로 부르지 않는다.
 * - 정답 방은 **"원래 방"**이다. '정답'이라는 말은 답을 아는 사람의 어휘라 스포일러로 읽힌다.
 * - 평서체(해라체)로 통일한다. 존댓말과 섞이면 같은 화면이 두 사람 목소리로 말한다.
 * - 짧게 쓴다. 설명하지 말고 다음 행동을 가리킨다.
 * - em대시(U+2014)와 en대시(U+2013)를 쓰지 않는다. 필요하면 마침표로 문장을 끊는다.
 *
 * ## 감식 어휘는 한 벌이다 (2026-09-02: "온에어"에서 컨셉 교체)
 *
 * 화면의 표식이 방송(REC·CAM·PLAYBACK)에서 **현장 감식**으로 바뀌었다. 게임 규칙과 1:1로
 * 맞물리기 때문이다 - 멀티에서 다른 사람이 몰래 두 곳을 바꾸고 가는 것이 문자 그대로
 * "누가 다녀갔다"이고, 플레이어가 하는 일(관찰·대조·표시)이 곧 감식이다.
 *
 * 쓰는 말은 **셋뿐이고 서로 겹치지 않는다.** 늘리면 같은 것을 부르는 이름이 둘이 된다.
 *
 * | 말   | 가리키는 것                    | 나오는 자리                       |
 * |------|--------------------------------|-----------------------------------|
 * | 현장 | 지금 보고 있는 방              | `hud.tag.scene` · `hud.tag.sceneNo` |
 * | 원본 | 손대기 전의 방(= 원래 방)      | `hud.tag.origin`                  |
 * | 대조 | 원본과 현장을 견주는 일        | `hud.origin.ready.sub`            |
 *
 * **무겁게 쓰지 않는다.** 파티게임이라 사건·수사·범죄의 어휘는 들여오지 않는다.
 * 가벼운 탐정 톤이 기준선이고, 긴장을 부추기는 말은 관찰 게임에서 그 자체로 방해다.
 *
 * ## 치환 문법
 *
 * - `{name}`         값을 그대로
 * - `{name:이/가}`   값 + 한국어 조사(받침 판정은 `index.ts`가 한다)
 * - `{n|one|other}`  영어 복수형. 한국어 사전에서는 쓰지 않는다.
 */

export const ko = {
  /* --- 공통 ---------------------------------------------------------------- */

  'common.appName': '이상한 방',
  'common.cancel': '취소',
  'common.close': '닫기',
  'common.none': '없음',
  /** 목록을 잇는 구분자. 가운뎃점 대신 쉼표를 쓴다(장식이 아니라 읽기 위한 것이다). */
  'common.separator': ', ',

  /* --- 언어 ---------------------------------------------------------------- */

  'lang.label': '언어',
  'lang.ko': '한국어',
  'lang.en': 'English',

  /* --- 동사 (이상한 곳의 종류) ------------------------------------------------- */
  /* 키는 `core/types.ts`의 `Verb` 값 그대로다. `t('verb.' + verb)`로 바로 찾는다. */

  'verb.add': '생김',
  'verb.remove': '없어짐',
  'verb.move': '옮김',
  'verb.rotate': '돌아감',
  'verb.recolor': '색 바뀜',

  /* --- 구획 이름 ------------------------------------------------------------ */
  /* 키는 `floorPlan.ts`의 zone id 그대로다. `t('room.' + zone.id)`. */

  'room.room': '방',
  'room.living': '거실',
  'room.kitchen': '주방',
  'room.bedroom': '침실',
  'room.bedroom-main': '안방',
  'room.bedroom-guest': '작은방',
  'room.hall': '복도',
  'room.bath': '욕실',
  /*
   * 아래 넷은 아파트 평면도(`DEFAULT_FLOOR_PLAN`)에만 있는 구획이다. 9·10단계와 자유 모드가
   * 그 평면도를 쓰므로, 이 키가 없으면 리빌 문장이 "study의 소파가 사라졌다"처럼
   * 평면도 데이터의 이름으로 떨어진다.
   */
  'room.study': '서재',
  'room.hall-west': '서쪽 복도',
  'room.hall-mid': '가운데 복도',
  'room.hall-east': '동쪽 복도',

  /* --- 타이틀 -------------------------------------------------------------- */
  /*
   * 앱의 첫 화면. 이름은 `common.appName`을 그대로 쓰므로 여기 키는 버튼 하나뿐이다.
   * 제목 키를 따로 만들면 게임 이름이 두 벌이 되고, 언젠가 한쪽만 고쳐진다.
   *
   * '시작하기'다. '들어가기'(`intro.start`)와 일부러 다른 말인데, 그쪽은 방에 들어가는
   * 것이고 이쪽은 게임을 켜는 것이라 같은 말을 쓰면 두 화면이 같은 일을 하는 것처럼 읽힌다.
   */

  'title.start': '시작하기',


  /* --- 메뉴 (로드맵 M13) ----------------------------------------------------- */

  'menu.eyebrow': '무엇을 할까',
  'menu.title': '이상한 방',
  'menu.go': '들어가기',
  'menu.back': '메뉴로',
  'menu.cardAria': '{title}. {status}',
  'menu.solo.eyebrow': '혼자',
  'menu.solo.title': '혼자 연습',
  'menu.solo.desc': '10단계를 순서대로 밟는다',
  'menu.solo.chipNext': '다음은 {step}단계',
  'menu.party.title': '모두의 방',
  'menu.party.desc': '같은 방을 여럿이 함께 수색한다',

  /* --- 단계 선택 ------------------------------------------------------------ */

  'select.eyebrow': '혼자 연습',
  'select.title': '이상한 방',
  'select.progress': '{cleared} / {total} 통과',
  'select.footInProgress': '하나 깨면 다음이 열린다',
  'select.footGraduated': '다 깼다. 이제 친구를 부르면 된다',
  'select.locked': '{step}단계를 깨면 열린다',
  'select.startHere': '여기부터',
  'select.noRecord': '아직 기록 없음',
  'select.best': '최고 {score}점, {found}/{total}, {time}',
  /*
   * 점수만 아는 기록. 못 깬 단계(최단 시간이 없다)와 발견 수를 적기 전의 옛 저장이 여기로 온다.
   * 없는 값을 0으로 메우면 "0/0"이 되는데 그건 기록이 아니라 거짓말이다(`ui/levelSelect.ts`).
   */
  'select.bestScoreOnly': '최고 {score}점',
  'select.stateCleared': '깬 단계',
  'select.stateCurrent': '지금 할 단계',
  'select.stateLocked': '잠긴 단계',
  'select.cardAria': '{step}단계 {name}. {state}. {status}',
  'select.chipOddities': '이상한 곳 {count}',
  'select.chipObjects': '물건 {count}개쯤',
  'select.chipTimeAria': '제한시간 {time}',

  /* --- 단계 이름과 힌트 ------------------------------------------------------- */
  /* 키는 `levels.ts`의 `LevelDef.id` 그대로다. `t('level.' + level.id + '.name')`. */

  'level.1.name': '첫 번째 방',
  'level.1.hint': '물건 하나가 없어진다. 비어 버린 자리를 쏴라.',
  'level.2.name': '하나 더 늘었다',
  'level.2.hint': '없던 물건도 생긴다. 빈 자리와 낯선 물건을 같이 봐라.',
  'level.3.name': '자리를 옮긴 것',
  'level.3.hint': '물건이 자리를 옮긴다. 개수만 세면 놓친다. 벽과의 간격을 봐라.',
  'level.4.name': '두 방 사이',
  'level.4.hint': '한 방을 다 본 뒤에 옆방으로 가라. 왔다 갔다 하면 둘 다 못 외운다.',
  'level.5.name': '돌아앉은 것',
  'level.5.hint': '물건이 방향을 튼다. 의자 앞면이 어디를 보고 있었나.',
  'level.6.name': '작은 집',
  'level.6.hint': '가구는 무리로 놓여 있다. 낱개 말고 무리째 외워라.',
  'level.7.name': '색이 바뀐 자리',
  'level.7.hint': '색이 바뀐다. 같은 물건이 여럿이면 서로 비교해 봐라.',
  'level.8.name': '원래 방을 보러 가기',
  'level.8.hint': '원래 방에 다녀올 수 있다. 대신 나오면 5초를 기다린다.',
  'level.9.name': '아파트',
  'level.9.hint': '아홉 칸이다. 남은 시간과 탄환을 보면서 순서를 정해라.',
  'level.10.name': '이상한 방',
  'level.10.hint': '여럿이 할 때와 똑같은 규칙이다. 여기까지 왔으면 준비는 끝났다.',

  /* --- 단계 안내 ------------------------------------------------------------ */

  'intro.step': '{order}단계',
  'intro.ruleOddities': '이상한 곳',
  'intro.ruleTime': '시간',
  'intro.ruleAmmo': '탄환',
  'intro.oddityValue': '{count}개',
  'intro.ammoValue': '{count}발',
  'intro.verbsLabel': '이번 단계에 나오는 이상한 곳',
  'intro.newBadge': '새로',
  'intro.start': '들어가기',
  'intro.back': '단계 고르기',

  /* --- 페이즈 -------------------------------------------------------------- */
  /*
   * 키는 `gameState.ts`의 `Phase` 값 그대로다.
   *
   * ## `stamp`는 두 페이즈에만 있다
   *
   * 페이즈가 바뀌는 순간 화면 가운데에 크게 찍히는 도장이다(띄우는 장치는 다른 파일이 만든다).
   * 리빌·결과에 키가 없는 것은 채우다 만 것이 아니라 **거기에는 도장이 없다는 뜻**이다 -
   * `skip`이 이미 쓰는 규약 그대로이며, 있고 없음은 `hasKey`가 가른다.
   *
   * 두 장이 짝을 이뤄 컨셉을 설명한다. 외우라고 시킨 방에 누가 손을 댔다는 것이
   * 문장 하나 없이 전달되고, 그것이 이 게임의 규칙 전부다. 그래서 두 장의 목소리가 다르다 -
   * 앞장은 시키는 말, 뒷장은 벌어진 일. 느낌표는 장식이 아니라 **말풍선의 문법**이다
   * (뾰족뾰족한 만화 말풍선 안에 크게 들어간다).
   */

  'phase.watch.name': '외우기',
  'phase.watch.hint': '방을 외워라',
  /**
   * 넘어가기 버튼이 **표**일 때의 얼굴(여럿이 판). `text`는 그 페이즈의 넘어가기 문구 그대로이고 뒤에 셈이 붙는다 -
   * 문구를 페이즈마다 다시 쓰지 않으려는 조립이다. 혼자인 판에서는 이 키를 지나지 않는다.
   */
  'phase.skip.votes': '{text} {votes}/{needed}',
  'phase.watch.skip': '바로 수색',
  /* 안내(`phase.watch.hint`)와 같은 동사를 쓴다. 도장과 안내가 다른 말을 시키면 둘 다 흐려진다. */
  'phase.watch.stamp': '외워라!',
  /*
   * 도장 문구 **세트**(로드맵 M9의 `stamp` 꾸밈). 조립 규칙은 `phase.<페이즈>.stamp.<꾸밈 id>`이고
   * 없으면 위의 기본으로 떨어진다(`ui/cosmeticVars.phaseStampKey`가 `hasKey`로 가른다).
   * 기본 세트(`stampBasic`)에 키가 없는 것이 규칙이다 - 아무것도 안 뽑은 화면이 지금 그대로여야 한다.
   * 세 세트의 목소리가 다르되 시키는 일은 같다: 외워라 / 손댔다 / 바꿔라.
   */
  'phase.watch.stamp.stampDetective': '눈에 담아라!',
  'phase.watch.stamp.stampDeadpan': '봐 둬라.',
  'phase.watch.stamp.stampLoud': '똑똑히 봐라!!',
  'phase.watch.stamp.stampProud': '또 이 방이군.',
  'phase.watch.stamp.stampRookie': "외웁니다, 전부!",
  'phase.watch.stamp.stampClerk': "지금 상태 확인.",
  'phase.watch.stamp.stampGuard': "눈으로 순찰.",
  'phase.watch.stamp.stampCourier': "받기 전에 확인!",
  'phase.watch.stamp.stampTeacher': "자, 잘 봐요.",
  'phase.watch.stamp.stampBoss': "외워 두게.",
  'phase.watch.stamp.stampReporter': "취재 시작!",
  'phase.watch.stamp.stampCleaner': "치우기 전에 봐.",
  'phase.watch.stamp.stampLibrarian': "조용히 외워요.",
  'phase.watch.stamp.stampScared': "눈 떼면 안 돼!",
  'phase.watch.stamp.stampPolite': "잘 봐 주세요.",
  'phase.watch.stamp.stampSleepy': "하암... 봐 둬.",
  'phase.watch.stamp.stampHasty': "빨리 외워!",
  'phase.watch.stamp.stampKind': "천천히 봐도 돼.",
  'phase.watch.stamp.stampBored': "뭐, 봐 두든가.",
  'phase.watch.stamp.stampFlustered': "어머, 외워 둬!",
  'phase.watch.stamp.stampShy': "저기... 봐 볼래?",
  'phase.watch.stamp.stampGrumpy': "똑바로 보라니까!",
  'phase.watch.stamp.stampPlayByPlay': "방을 봅니다!",
  'phase.watch.stamp.stampBreakingNews': "현장을 보십시오.",
  'phase.watch.stamp.stampAd': "놓치지 마세요!",
  'phase.watch.stamp.stampAnnouncement': "살펴보시기 바랍니다.",
  'phase.watch.stamp.stampPoem': "오래 바라보아라.",
  'phase.watch.stamp.stampFolkTale': "잘 보아 두거라.",
  'phase.watch.stamp.stampManual': "1단계. 살펴보기.",
  'phase.watch.stamp.stampDiary': "잘 기억해 두자.",
  'phase.watch.stamp.stampMachine': "관측 개시.",
  'phase.hunt.name': '수색',
  'phase.hunt.hint': '이상한 곳을 쏴라',
  'phase.hunt.skip': '그만 찾기',
  /* 이 한 줄이 컨셉의 이름이다. 규칙을 설명하지 않고 방금 일어난 일만 말한다. */
  'phase.hunt.stamp': '누가 다녀갔다!',
  'phase.hunt.stamp.stampDetective': '현장이다!',
  'phase.hunt.stamp.stampDeadpan': '뭔가 바뀌었다.',
  'phase.hunt.stamp.stampLoud': '누가 왔다 갔다!!',
  'phase.hunt.stamp.stampProud': '다 보인다.',
  'phase.hunt.stamp.stampRookie': "어, 바뀌었어요!",
  'phase.hunt.stamp.stampClerk': "이건 접수합니다.",
  'phase.hunt.stamp.stampGuard': "이상 있음!",
  'phase.hunt.stamp.stampCourier': "누가 두고 갔다!",
  'phase.hunt.stamp.stampTeacher': "뭐가 달라졌죠?",
  'phase.hunt.stamp.stampBoss': "누가 손댔나?",
  'phase.hunt.stamp.stampReporter': "이거 특종이다!",
  'phase.hunt.stamp.stampCleaner': "누가 어질렀네.",
  'phase.hunt.stamp.stampLibrarian': "제자리가 아니네요.",
  'phase.hunt.stamp.stampScared': "바뀌었어, 어떡해!",
  'phase.hunt.stamp.stampPolite': "뭔가 바뀌었어요.",
  'phase.hunt.stamp.stampSleepy': "음, 바뀌었네.",
  'phase.hunt.stamp.stampHasty': "바뀌었어, 찾아!",
  'phase.hunt.stamp.stampKind': "뭔가 달라졌어.",
  'phase.hunt.stamp.stampBored': "바뀌었나 보네.",
  'phase.hunt.stamp.stampFlustered': "세상에, 바뀌었어!",
  'phase.hunt.stamp.stampShy': "누가 왔나 봐.",
  'phase.hunt.stamp.stampGrumpy': "누가 건드렸어!",
  'phase.hunt.stamp.stampPlayByPlay': "아, 바뀌었습니다!",
  'phase.hunt.stamp.stampBreakingNews': "속보입니다.",
  'phase.hunt.stamp.stampAd': "새로워졌습니다!",
  'phase.hunt.stamp.stampAnnouncement': "달라진 곳이 있습니다.",
  'phase.hunt.stamp.stampPoem': "무언가 옮겨 갔다.",
  'phase.hunt.stamp.stampFolkTale': "간밤에 바뀌었더라.",
  'phase.hunt.stamp.stampManual': "2단계. 이상한 곳.",
  'phase.hunt.stamp.stampDiary': "어제와 달랐다.",
  'phase.hunt.stamp.stampMachine': "오차 검출.",
  'phase.reveal.name': '리빌',
  'phase.reveal.hint': '못 찾은 곳',
  'phase.reveal.skip': '건너뛰기',
  'phase.result.name': '결과',
  'phase.result.hint': '',

  /*
   * 여럿이의 세 페이즈(로드맵 M2). `HudPhase`가 셋을 더했고 HUD는 같은 조립 키(`phase.<p>.name`)로 그린다.
   * 이름은 용어집 1.5절(꾸미기 / 바꾸기)이고, 대기는 '방'의 말이라 "로비"를 쓰지 않는다.
   * 바꾸기 안내는 **찾는 사람이 보는 문장**이다 - 체인저는 M4에서 자기 작업대 문구를 갖는다.
   * 도장은 바꾸기에만 있다(외우기·수색과 같은 규칙: 있고 없음이 곧 그 페이즈에 도장이 있는가).
   */
  'phase.lobby.name': '준비',
  'phase.lobby.hint': '다 모이면 시작한다',
  'phase.lobby.skip': '바로 시작',
  'phase.decorate.name': '꾸미기',
  'phase.decorate.hint': '소품을 놓아라',
  'phase.decorate.skip': '다 놓았다',
  'phase.change.name': '바꾸기',
  /* M11에서 전원이 자기 구획을 고친다 - '다녀간 사람이 손대는 중'은 남을 기다리던 시절의 말이라 갈았다. */
  'phase.change.hint': '네 구획을 손봐라',
  'phase.change.skip': '바로 외우기',
  'phase.change.stamp': '바꿔라!',
  'phase.change.stamp.stampDetective': '단서를 남겨라!',
  'phase.change.stamp.stampDeadpan': '손대라.',
  'phase.change.stamp.stampLoud': '다 뒤집어라!!',
  'phase.change.stamp.stampProud': '어디 찾아봐라.',
  'phase.change.stamp.stampRookie': "제가 바꿔 볼게요!",
  'phase.change.stamp.stampClerk': "한 줄 고치세요.",
  'phase.change.stamp.stampGuard': "자리 옮길 것.",
  'phase.change.stamp.stampCourier': "하나 두고 가라!",
  'phase.change.stamp.stampTeacher': "한 개만 바꿔요.",
  'phase.change.stamp.stampBoss': "자네가 바꾸게.",
  'phase.change.stamp.stampReporter': "기삿거리 만들어!",
  'phase.change.stamp.stampCleaner': "좀 어질러 봐.",
  'phase.change.stamp.stampLibrarian': "하나만 옮겨요.",
  'phase.change.stamp.stampScared': "나도 바꿔야 해?",
  'phase.change.stamp.stampPolite': "바꿔 주시겠어요?",
  'phase.change.stamp.stampSleepy': "바꾸고 자자.",
  'phase.change.stamp.stampHasty': "얼른 바꿔!",
  'phase.change.stamp.stampKind': "네 차례야, 바꿔.",
  'phase.change.stamp.stampBored': "아무거나 바꿔.",
  'phase.change.stamp.stampFlustered': "어서 바꿔 봐!",
  'phase.change.stamp.stampShy': "나도... 바꿀래.",
  'phase.change.stamp.stampGrumpy': "이번엔 네가 바꿔!",
  'phase.change.stamp.stampPlayByPlay': "손이 나갑니다!",
  'phase.change.stamp.stampBreakingNews': "생중계 중, 바꾸세요.",
  'phase.change.stamp.stampAd': "지금 바꾸세요!",
  'phase.change.stamp.stampAnnouncement': "바꿔 주시기 바랍니다.",
  'phase.change.stamp.stampPoem': "너도 하나 옮겨라.",
  'phase.change.stamp.stampFolkTale': "네가 바꾸거라.",
  'phase.change.stamp.stampManual': "3단계. 바꾸기.",
  'phase.change.stamp.stampDiary': "나도 하나 바꾼다.",
  'phase.change.stamp.stampMachine': "수정 허가.",

  /* --- 인게임 HUD ----------------------------------------------------------- */

  'hud.found': '찾음',
  'hud.foundOf': '{found} / {total}',
  'hud.ammoUnit': '발',
  'hud.ammoLow': '얼마 안 남았다',
  /*
   * 잔탄 0이 라운드의 끝을 뜻하는 것은 **쓸 탄창까지 없을 때뿐이다**(`gameState.onMiss`).
   * 탄창이 남아 있으면 화면은 끝이 아니라 할 일을 말해야 하며, 그 문장은 `hud.mag.reload`다.
   * 두 상황이 같은 문장을 쓰면 사람은 게임이 멈춘 줄 알고 판을 버린다.
   */
  'hud.ammoEmpty': '다 썼다. 이번 판은 여기까지',
  /* 재장전 순간의 자막. 0.5초 안에 사라지므로 읽는 글이 아니라 **보는 숫자**다. */
  'hud.ammoGain.label': '+{amount}발',
  'hud.ammoGain.announce': '탄창을 썼다. 탄환 {amount}발이 찼다. 이제 {ammo}발',
  'hud.ammoBlocked': '원래 방에서는 못 쏜다',

  /* --- 탄창 ----------------------------------------------------------------- */
  /*
   * 계정이 들고 있는 보조 자원(`game/magazines.ts`). **없어도 모든 단계를 클리어할 수 있다**는
   * 것이 밸런스의 전제라, 여기 문장은 하나도 조르지 않는다. 보유량 0을 나무라는 문구를 두면
   * 그 순간 화면이 "돈을 내라"고 말하게 되고, 튜토리얼에서 그 말을 듣는 사람은 다시 안 온다.
   */

  /* 칩의 이름. 상태에 따라 바뀌지 않는다 - 이름이 바뀌는 버튼은 다른 버튼으로 읽힌다. */
  'hud.mag.label': '탄창',
  /* 화면에는 숫자만 있으므로 소리로는 뜻과 할 일을 함께 말한다. */
  'hud.mag.aria': '탄창 {count}개, 눌러서 탄환 채우기',
  'hud.mag.reload': '탄창을 써라',
  /* 가진 것은 있는데 이번 판에 못 쓴다(`tuning.maxMagazinesPerRound`). "없다"와 다른 말이다. */
  'hud.mag.roundSpent': '이번 판에 쓸 탄창은 다 썼다',
  /*
   * 탄창이 **통째로** 들어갈 자리가 없다(`gameState.hasRoomForFullMagazine`).
   *
   * 규칙이 "가득이면 거부"에서 "빈 자리가 탄창 하나보다 작으면 거부"로 바뀌면서 이 문장이
   * 필요해졌다. 기본값(상한 16 · 탄창 6)에서 잔탄 11~15는 가득도 아니고 쓸 수도 없는 구간이라,
   * "탄환이 가득이다"로는 설명이 되지 않는다 - 사람은 숫자를 보고 있는데 화면이 딴말을 한다.
   *
   * 상태가 아니라 **다음 행동**을 말한다. 몇 발을 더 쓰면 되는지가 곧 할 일이고, 그 수는
   * 화면이 아는 값(상한 - 잔탄)과 탄창 회복량에서 나온다.
   */
  'hud.mag.needRoomCount': '{count}발 더 쓰면 탄창을 넣을 수 있다',
  /*
   * 회복량을 모르는 경우의 같은 말. 숫자를 지어내지 않고 방향만 말한다
   * (`ui/gameHud.ts`의 `GameHudView.ammoPerMagazine` 주석에 왜 모를 수 있는지가 있다).
   */
  'hud.mag.needRoom': '탄창은 자리가 남아야 들어간다. 더 쏘고 눌러라',
  'hud.originHint': '원래 방이라 못 쏜다',
  /*
   * 방을 갈아 끼우는 0.4초 동안 눌렸다. 조준은 아직 **직전 방**을 보고 판정은 이미 바뀐
   * 상태로 도는 구간이라 그 한 발은 판정에 넣지 않는데(`game/session.ts`의 `shootAt`),
   * 조용히 버리면 버튼이 고장 난 것으로 읽힌다. 그래서 이유를 그 자리에 띄운다.
   */
  'hud.warpHint': '방이 바뀌는 중이라 못 쏜다',
  'hud.originBadge': '원래 방',
  'hud.origin.ready.label': '원래 방',
  /* 리필이 사라진 뒤 이 방에 가는 이유는 하나뿐이다 - 대조. 문구가 그 하나만 말한다. */
  'hud.origin.ready.sub': '원래 모습과 대조',
  'hud.origin.staying.label': '나가기 {seconds}',
  'hud.origin.staying.sub': '곧 자동으로 나온다',
  'hud.origin.cooldown.label': '{seconds}초 뒤',
  /* 쿨타임이 짧을 때의 문구. 초를 세지 않는 이유는 `gameHud.ts`의 `BRIEF_COOLDOWN_MS` 주석에. */
  'hud.origin.cooldown.brief': '곧 열린다',
  'hud.origin.cooldown.sub': '아직 못 들어간다',

  /* --- 현장 표식 ------------------------------------------------------------ */
  /*
   * 화면 구석의 작은 표식 셋. 자리와 모양은 방송 시절(REC · PLAYBACK · CAM 01) 그대로이고
   * **뜻만 감식으로 바뀌었다** - 붉은 점은 지금 서 있는 곳을, 워터마크는 현장 번호를 말한다.
   *
   * 세 값 모두 짧아야 한다. 정보가 아니라 표식이라, 길어지는 순간 읽어야 하는 글이 되고
   * 그때 이 셋은 방을 보는 눈을 뺏는다.
   */

  /** 지금 보고 있는 방. 외우기든 수색이든 사람이 서 있는 곳은 언제나 현장이다. */
  'hud.tag.scene': '현장',
  /** 원래 방에 들어가 있는 동안. 손대기 전의 방을 보고 있다는 뜻이다. */
  'hud.tag.origin': '원본',
  /** 하단 가운데 워터마크. 방송 채널 번호가 있던 자리이며 지금은 현장 번호다. */
  'hud.tag.sceneNo': '이상한 방 현장 01',

  /* --- 콤보 칩 -------------------------------------------------------------- */
  /*
   * 배수 규칙(상한 3.0 포함)은 `game/combo.ts` 하나가 소유한다. 여기 있는 것은 그 값을
   * 읽어 주는 말뿐이다. MAX는 배수가 상한에 닿았다는 뜻이지 콤보가 멈췄다는 뜻이 아니다.
   */

  'hud.combo.label': '{count}연속',
  'hud.combo.labelCap': '{count}연속 MAX',

  /* --- 걸기 칩 (로드맵 M7) ------------------------------------------------------ */
  /*
   * 판돈 올리기는 '걸기'다(GLOSSARY 1.5 - 스냅·배팅·올인·도박은 쓰지 않는다). 조건(잔탄 · 한 판에 한 번)은
   * `game/raise.ts`가 소유하고 칩(`ui/raiseChip.ts`)은 뷰의 불리언(`canRaise` · `raised`)만 읽는다 - 그래서 잠긴 이유에
   * 숫자("4발")를 적지 않는다. 숫자를 적으면 `?raiseMin=`을 돌린 날 문장이 거짓말을 한다. 같은 이유로 aria에도 빗나감의 발 수를
   * 적지 않는다(`raiseMissCost`는 `?raiseCost=`로 바뀐다). '두 배'는 상수(`RAISE_MULTIPLIER`)라 괜찮고 배수 표기(×2)는 칩이 그린다.
   */

  'raise.chip.label': '걸기',
  'raise.chip.on': '걸었다',
  'raise.chip.aria': '걸기. 다음 발견부터 점수 두 배, 빗나가면 탄환이 더 든다',
  'raise.chip.onAria': '걸었다. 발견 점수 두 배, 빗나가면 탄환이 더 든다',
  'raise.chip.lockedAria': '걸기. {reason}',
  'raise.lock.already': '이미 걸었다. 한 판에 한 번이다',
  'raise.lock.ammo': '탄환이 모자라서 못 건다',
  /** 걸기가 성립한 순간의 토스트(싱글 · 여럿이의 내 것). 여럿이 전원에게는 배너(`wager.banner`)가 따로 간다. */
  'raise.notice.self': '걸었다. 다음 발견부터 두 배다',

  /* --- 설정 ----------------------------------------------------------------- */
  /*
   * 톱니바퀴 하나에 모인 항목들(`ui/settings.ts`). **상태를 글자로 쓰지 않는다** -
   * 스피커에 빗금이 그어지면 꺼진 것이고, 그 옆에 "끔"이라고 또 쓰는 것은 중복이다.
   * 그래서 여기 있는 것은 **항목 이름**과 **화면에 안 보이는 접근성 문장**뿐이다.
   *
   * `settings.aria.on/off`가 `{name}`을 받는 것은 항목이 늘어나기 때문이다. 항목마다
   * 문장을 한 쌍씩 적어 두면 새 항목을 넣을 때마다 사전 두 곳을 함께 고쳐야 하고,
   * 그 둘 중 하나를 빠뜨린 항목은 낭독기에서만 이름 없는 버튼이 된다.
   */

  'settings.title': '설정',
  'settings.open': '설정 열기',
  'settings.sound': '효과음',
  'settings.bgm': 'BGM',
  'settings.haptics': '진동',
  'settings.language': '언어',
  'settings.aria.on': '{name} 켜짐. 눌러서 끄기',
  'settings.aria.off': '{name} 꺼짐. 눌러서 켜기',
  'settings.aria.choice': '{name}. 지금 {value}. 눌러서 {next}',
  /*
   * 효과음만 자기 문장을 갖는다. 음소거 버튼이 화면 구석에 혼자 서 있던 시절의 키를 그대로
   * 이어받은 것이며, 항목별 문장이 필요할 때 어떻게 넣는지의 예시이기도 하다
   * (`settings.ts`의 `ariaOnKey`). '소리'가 아니라 '효과음'인 이유는 이제 BGM도 소리이기 때문이다.
   */
  'hud.sound.aria.on': '효과음 켜짐. 눌러서 끄기',
  'hud.sound.aria.off': '효과음 꺼짐. 눌러서 켜기',

  /* --- 사격 피드백 ---------------------------------------------------------- */
  /* 키는 `ShotMarkKind` 값 그대로다. label은 화면에, announce는 스크린리더에 간다. */

  'shot.object.label': '찾았다',
  'shot.object.announce': '이상한 곳을 하나 찾았다',
  'shot.removed.label': '없어진 자리',
  'shot.removed.announce': '없어진 자리를 찾았다',
  'shot.miss.label': '-1발',
  'shot.miss.announce': '빗나갔다. 탄환이 하나 줄었다',
  'shot.already-found.label': '이미 찾았다',
  'shot.already-found.announce': '이미 찾은 곳이다',
  'shot.dry.label': '탄환 없음',
  'shot.dry.announce': '탄환이 없어 쏠 수 없다',
  /*
   * 여럿이의 회색 표식. 판정이 오기 전이라 맞았다고도 빗나갔다고도 말하지 않는다(`ShotMarkKind.pending`).
   * `label`은 `shot.<kind>.label` 조립 키의 완결성용이다 - 세션이 빈 라벨을 넘겨 화면에는 고리만 남는다(`matchSession.shootAt`).
   * `announce`(스크린리더)만 실제로 읽힌다.
   */
  'shot.pending.label': '확인 중',
  'shot.pending.announce': '쏘았다. 확인 중',

  /* --- 발견 연출 자막 (`ui/juice.ts`) ------------------------------------------ */
  /*
   * 히트 자막은 콤보 단계로 갈린다(0부터 3까지 넷). 다섯 개 이상 두지 않는 이유는
   * 한 판의 발견이 열두 번뿐이라, 그보다 잘게 쪼개면 한 판에 한 번도 안 나오는 문구가 생겨
   * "그 말을 본 적 있다"는 기억이 남지 않기 때문이다.
   */

  'juice.hit.0': '찾았다',
  'juice.hit.1': '연속',
  'juice.hit.2': '불붙었다',
  'juice.hit.3': '못 말린다',
  'juice.comboBreak': '콤보 끊김',
  'juice.finale.title': '전부 찾았다',
  'juice.finale.sub': '이상한 곳 {count}개',
  'juice.perfect.title': '퍼펙트',
  'juice.perfect.sub': '한 발도 안 틀렸다',
  'juice.perfect.bonus': '보너스 {bonus}점',
  /*
   * 여럿이 리빌의 대형 스탬프(로드맵 M5). 미발견 중 **마지막**(= 가장 쉬웠는데 다 놓친 것)에만 찍히므로 아랫줄의 "제일 쉬운
   * 거였다"는 순서가 보장하는 사실이다(`revealOrder` 'easiest'). 웃음의 주어가 한 사람에서 전원으로 옮겨 가는 자리다(컨셉시트 5절 6번).
   */
  'juice.nobodySaw.title': '아무도 못 봤다!',
  'juice.nobodySaw.sub': '이게 제일 쉬운 거였다',

  /* --- 수색 중 알림 ---------------------------------------------------------- */

  'notice.remaining': '{count}개 남았다',

  /* --- 리빌 문장 ------------------------------------------------------------ */
  /*
   * 구획 이름을 아는 경우와 모르는 경우를 따로 둔다. 이어붙이면 영어에서 어순이 깨진다.
   * 돌아감과 색 바뀜은 주어를 물건이 아니라 속성으로 잡는다("소파 방향이 바뀌었다").
   *
   * 다섯 줄 모두 **물건에게 일어난 일**로 적는다("사라졌다", "생겼다", "옮겨졌다", "바뀌었다").
   * 이 게임의 전제가 누군가 방을 건드렸다는 것이라, 물건이 스스로 한 일처럼 쓰면
   * ("자리를 옮겼다") 다섯 줄 중 하나만 목소리가 달라진다.
   * 단계 3의 이름이 '자리를 옮긴 것'인 것은 그 단계를 사람에게 소개하는 말이라 별개다.
   */

  'reveal.add.inRoom': '{room}의 {item:이/가} 새로 생겼다',
  'reveal.add.plain': '{item:이/가} 새로 생겼다',
  'reveal.remove.inRoom': '{room}의 {item:이/가} 사라졌다',
  'reveal.remove.plain': '{item:이/가} 사라졌다',
  'reveal.move.inRoom': '{room}의 {item:이/가} 옮겨졌다',
  'reveal.move.plain': '{item:이/가} 옮겨졌다',
  'reveal.rotate.inRoom': '{room}의 {item} 방향이 바뀌었다',
  'reveal.rotate.plain': '{item} 방향이 바뀌었다',
  'reveal.recolor.inRoom': '{room}의 {item} 색이 바뀌었다',
  'reveal.recolor.plain': '{item} 색이 바뀌었다',
  /** 카탈로그에 없는 물건. 리빌이 예외로 죽는 것보다 무엇이라도 비추는 편이 낫다. */
  'reveal.unknownItem': '무언가',
  /*
   * 여럿이 리빌의 이름표(로드맵 M5, `ui/revealParty.ts`). 리빌 문장 아래 한 줄로 **누가** 찾았는지를 말한다 - 문장에 이름을
   * 섞지 않고 따로 두는 이유는 `reveal.RevealStep.foundBy` 주석에 있다. 이름은 이름표라 사전을 거치지 않고 `{name}`으로 받는다.
   * 내 것은 "내가 찾았다"다 - 내 화면에 내 이름표를 찍으면 남의 화면과 얼굴이 같아 "나"가 안 읽힌다.
   */
  'reveal.party.foundBy': '{name:이/가} 찾았다',
  'reveal.party.mine': '내가 찾았다',
  'reveal.party.nobody': '아무도 못 봤다',

  /* --- 소품 이름 -------------------------------------------------------------- */

  /*
   * 카탈로그 151종의 화면 이름. 조립 키는 `item.<카탈로그 id>`이고 부르는 자리는 `i18n/itemName.ts` 하나다.
   * `catalog/`의 `name`은 에디터·디버그용 키 문자열이지 화면 문구가 아니다 - 그 값이 문장에 그대로 실려
   * 영어에 한국어가 새던 것을 여기로 옮겼다(로드맵 7절 P2의 남은 것).
   *
   * `택배 박스`가 카탈로그의 `택배상자`와 다른 이유는 **`상자`가 사전 금지어**이기 때문이다(다른 게임의
   * 어휘). 같은 카탈로그의 `피자박스`가 이미 그 선례다.
   */
  'item.mug': '머그컵',
  'item.book': '책',
  'item.trash-bin': '휴지통',
  'item.potted-plant': '화분',
  'item.chair': '의자',
  'item.bench': '벤치',
  'item.table-dining': '식탁',
  'item.shelf-tall': '책장',
  'item.sofa': '소파',
  'item.rug-round': '원형 러그',
  'item.cup': '컵',
  'item.plate': '접시',
  'item.bottle': '병',
  'item.computer-mouse': '마우스',
  'item.keyboard': '키보드',
  'item.doormat': '현관매트',
  'item.pillow': '쿠션',
  'item.laptop': '노트북',
  'item.microwave': '전자레인지',
  'item.lamp-table': '탁상등',
  'item.nightstand': '협탁',
  'item.monitor': '모니터',
  'item.cardboard-box': '택배 박스',
  'item.stool-bar': '바스툴',
  'item.speaker': '스피커',
  'item.mirror': '거울',
  'item.side-table': '사이드테이블',
  'item.cabinet-tv': 'TV장',
  'item.television': '텔레비전',
  'item.desk': '책상',
  'item.kitchen-cabinet': '주방수납장',
  'item.kitchen-stove': '가스레인지',
  'item.toilet': '변기',
  'item.bathroom-sink': '세면대',
  'item.chair-desk': '데스크체어',
  'item.plant-large': '큰 화분',
  'item.lamp-floor': '플로어램프',
  'item.bookcase-open': '오픈책장',
  'item.wardrobe': '옷장',
  'item.coat-rack': '코트걸이',
  'item.fridge': '냉장고',
  'item.bed-double': '침대',
  'item.bathtub': '욕조',
  'item.rug-rectangle': '사각 러그',
  'item.chair-rounded': '아치의자',
  'item.chair-cantilever': '캔틸레버 의자',
  'item.bench-backrest': '등받이 벤치',
  'item.stool-bar-square': '사각 바스툴',
  'item.armchair': '안락의자',
  'item.recliner': '리클라이너',
  'item.lounge-chair-wide': '와이드 라운지체어',
  'item.ottoman': '오토만',
  'item.sofa-chaise': '카우치 소파',
  'item.sofa-corner': '코너 소파',
  'item.sofa-modern': '모던 소파',
  'item.bed-single': '싱글 침대',
  'item.bed-bunk': '이층침대',
  'item.nightstand-legs': '다리형 협탁',
  'item.pillow-long': '긴 쿠션',
  'item.coat-hooks': '벽걸이 옷걸이',
  'item.table-round': '육각 식탁',
  'item.table-glass': '유리 식탁',
  'item.table-cross': 'X다리 식탁',
  'item.table-cloth': '천 덮인 식탁',
  'item.desk-corner': 'ㄱ자 책상',
  'item.table-coffee': '커피테이블',
  'item.table-coffee-square': '정사각 커피테이블',
  'item.table-coffee-glass': '유리 커피테이블',
  'item.table-coffee-glass-square': '정사각 유리 커피테이블',
  'item.bookcase-open-low': '낮은 오픈선반',
  'item.bookcase-doors': '여닫이 책장',
  'item.cabinet-tv-doors': '여닫이 TV장',
  'item.cardboard-box-open': '열린 택배 박스',
  'item.kitchen-cabinet-corner': '주방 코너장',
  'item.kitchen-cabinet-upper': '주방 상부장',
  'item.kitchen-cabinet-upper-low': '낮은 상부장',
  'item.kitchen-cabinet-upper-corner': '코너 상부장',
  'item.bathroom-cabinet': '욕실 거울장',
  'item.bathroom-vanity': '세면 수납장',
  'item.lamp-square-table': '사각 탁상등',
  'item.kitchen-sink': '싱크대',
  'item.fridge-small': '소형 냉장고',
  'item.fridge-large': '양문형 냉장고',
  'item.fridge-built-in': '빌트인 냉장고',
  'item.kitchen-bar': '바 카운터',
  'item.kitchen-bar-end': '반원 바카운터',
  'item.range-hood': '대형 레인지후드',
  'item.range-hood-modern': '모던 레인지후드',
  'item.toaster': '토스터',
  'item.blender': '믹서기',
  'item.coffee-machine': '커피머신',
  'item.washer': '세탁기',
  'item.dryer': '건조기',
  'item.washer-dryer-tower': '세탁건조기 타워',
  'item.shower-booth': '샤워부스',
  'item.shower-booth-round': '곡면 샤워부스',
  'item.bathroom-sink-square': '사각 세면대',
  'item.television-vintage': '브라운관 TV',
  'item.television-antenna': 'TV 안테나',
  'item.radio': '라디오',
  'item.speaker-small': '북셸프 스피커',
  'item.ceiling-fan': '실링팬',
  'item.lamp-ceiling-square': '천장등',
  'item.lamp-wall': '벽등',
  'item.bear-trophy': '곰 머리 박제',
  'item.lamp-floor-square': '기둥 플로어램프',
  'item.rug-square': '정사각 러그',
  'item.rug-oval': '타원 러그',
  'item.cup-coffee': '커피잔',
  'item.glass': '유리컵',
  'item.glass-wine': '와인잔',
  'item.plate-deep': '오목접시',
  'item.bowl': '볼',
  'item.bowl-cereal': '시리얼볼',
  'item.pot': '냄비',
  'item.pot-lid': '냄비뚜껑',
  'item.pan': '팬',
  'item.frying-pan': '프라이팬',
  'item.cutting-board': '도마',
  'item.knife-block': '칼블록',
  'item.utensil-fork': '포크',
  'item.utensil-spoon': '숟가락',
  'item.whisk': '거품기',
  'item.mortar': '절구',
  'item.rolling-pin': '밀대',
  'item.egg-cup': '에그컵',
  'item.cupcake': '컵케이크',
  'item.bottle-ketchup': '케첩병',
  'item.can': '통조림',
  'item.can-small': '납작 통조림',
  'item.soda-bottle': '음료병',
  'item.carton': '우유팩',
  'item.barrel': '나무통',
  'item.bag': '종이봉투',
  'item.shaker-salt': '소금통',
  'item.pepper-mill': '후추그라인더',
  'item.honey': '꿀단지',
  'item.peanut-butter': '땅콩버터',
  'item.apple': '사과',
  'item.banana': '바나나',
  'item.bread': '식빵',
  'item.loaf': '빵덩이',
  'item.cake': '케이크',
  'item.cookie': '쿠키',
  'item.croissant': '크루아상',
  'item.donut': '도넛',
  'item.muffin': '머핀',
  'item.sandwich': '샌드위치',
  'item.pizza-box': '피자박스',
  'item.pumpkin': '호박',
  'item.watermelon': '수박',

  /* --- 결과 ---------------------------------------------------------------- */

  'result.eyebrow': '한 판 끝',
  'result.eyebrowLevel': '{order}단계 {name}',
  'result.attempt': '{n}회차',
  'result.end.all-found': '전부 찾았다',
  'result.end.out-of-ammo': '탄환 소진',
  /* 여럿이. 한 사람의 잔탄 0은 관전이고 **전원**이 관전이어야 끝난다(용어집 1.5절 '관전'). */
  'result.end.all-out': '전원 탄환 소진',
  /* 여럿이. 남은 이상한 곳이 전부 마지막 술래의 제 구획 것이라 아무도 찾을 수 없다(M11 규칙 4). */
  'result.end.stalled': '더 찾을 사람이 없다',
  'result.end.timeout': '시간 초과',
  'result.end.skipped': '중단',
  'result.headline.perfect': '완벽하다',
  'result.headline.most': '거의 다 봤다',
  'result.headline.half': '절반은 찾았다',
  'result.headline.few': '아직 눈이 안 떠졌다',
  'result.headline.none': '이건 다시 해야 한다',
  'result.headline.fallback': '한 판 끝',
  /*
   * 다녀간 사람의 결과 머리글(여럿이 M5). 찾는 사람의 얼굴("찾음 0/12")은 다녀간 사람에게 거짓이라 - 그 사람의 성적은
   * **아무도 못 찾은 개수**다(`matchScore.changerRoundScore`). 0이면 전부 들킨 것이고, 그것도 격려 톤으로 말한다(3절).
   */
  'result.visitor.headline': '{count}개는 아무도 못 찾았다',
  'result.visitor.headlineNone': '전부 들켰다',
  'result.cleared': '클리어',
  'result.fail.one': '하나만 더',
  'result.fail.two': '거의 다 왔다',
  'result.fail.few': '감이 오고 있다',
  'result.fail.many': '한 번 더 보면 보인다',
  'result.fail.fallback': '한 번 더',
  'result.goalFail': '{remaining}개만 더 찾으면 클리어. 기준은 {min}개, 이상한 곳은 {total}개',
  'result.goalCleared': '기준 {min}개 중 {found}개 찾음',
  'result.scoreUnit': '점',
  /*
   * 점수 내역 넉 줄. 순서가 곧 이 게임의 점수 구조다 -
   * 기본(발견) → 배수가 얹은 몫(콤보) → 시간(속도) → 무오답(퍼펙트).
   * 네 줄과 합계가 한 계산(`hunt.computeScoreBreakdown`)에서 나오므로 산수가 언제나 맞는다.
   */
  'result.score.find': '발견',
  'result.score.combo': '콤보',
  'result.score.speed': '속도',
  'result.score.perfect': '퍼펙트',
  /** 걸기(M7)가 얹어 준 몫(`hunt.ScoreBreakdown.raisedBonus`). 0이면 줄이 없다 - 걸지 않은 판에 "걸기 +0"은 채근이다. */
  'result.score.raise': '걸기',
  'result.statFound': '찾음',
  'result.statCombo': '최고 연속',
  'result.comboValue': '{count}연속',
  'result.statAmmo': '남은 탄환',
  'result.statTime': '걸린 시간',
  /* 총점 옆의 도장. 헤드라인이 아니라 점수 줄에 붙는 이유는 그것이 **점수의 일부**이기 때문이다. */
  'result.perfectBadge': '퍼펙트',
  'result.missedTitle': '놓친 곳',
  'result.missedTitleCount': '놓친 곳 {count}개',
  'result.missedEmpty': '하나도 안 놓쳤다',
  /*
   * 방 번호와 "이 판 링크"가 여기 있었다(2026-09-02 제거).
   *
   * 같은 방을 겨루라고 만든 시드 공유였는데 그럴 자리가 아직 없고, 결과 화면에서 그 줄은
   * **"한 판 더"로 가는 길에 놓인 장애물**이었다(이 화면의 유일한 목적이 그 버튼이다).
   * 시드 자체는 게임이 계속 쓴다 - 사라진 것은 표시뿐이다.
   */
  'result.playAgain': '한 판 더',
  'result.retryFree': '같은 방, 다른 곳',
  'result.retryLevel': '다른 곳으로 다시',
  'result.nextLevel': '다음 단계',
  'result.exit': '단계 고르기',
  'result.shortcut': '{key} = {label}',

  /* --- 꾸미기 (배치 화면) ----------------------------------------------------- */

  'editor.catalogTitle': '소품',
  'editor.selectTool': '고르기',
  'editor.colorTitle': '색',
  'editor.colorAria': '{name} 색',
  'editor.rotateLockedChip': '안 돌아감',
  'editor.controlsTitle': '조작',
  'editor.statusTitle': '지금',
  'editor.statPlaced': '놓은 것',
  'editor.statSelected': '고른 것',
  'editor.statFacing': '방향',
  'editor.statHeight': '높이',
  'editor.propCount': '소품',
  'editor.roomCode': '방 번호',
  'editor.modePlace': '놓기',
  'editor.modeLook': '보기',
  'editor.toLookMode': '보기 모드로 바꾼다. 탭하면 쏜다',
  'editor.toPlaceMode': '놓기 모드로 바꾼다. 탭하면 자리를 정한다',
  /* 모드 배지의 둘째 줄. 위 두 문장에서 뒷마디만 떼어 낸 말이라 두 곳이 갈라지지 않는다. */
  'editor.tapShoots': '탭하면 쏜다',
  'editor.tapPlaces': '탭하면 자리를 정한다',
  'editor.pickPrompt': '놓을 소품을 고르세요',
  'editor.confirmPlace': '놓기',
  'editor.confirmMove': '옮기기',
  'editor.removeSelected': '고른 물건 치우기',
  'editor.rotateSelected': '소품 90도 돌리기',
  'editor.heightFloating': '공중 {level}칸',
  'editor.heightOnSurface': '바닥에 붙음',
  'editor.facingLocked': '어느 쪽으로 놔도 똑같다',
  'editor.facingFree': 'R 키로 돌린다',
  'editor.viewLeft': '시점 왼쪽으로 90도',
  'editor.viewRight': '시점 오른쪽으로 90도',
  'editor.zoomIn': '확대',
  'editor.zoomOut': '축소',
  'editor.zoom': '줌',
  'editor.footprint': '{name}, {cells}칸',

  /* --- 배치 거절 이유 -------------------------------------------------------- */
  /* 키는 거절 사유 id 그대로다. 왜 안 되는지 말하지 않으면 고장으로 읽힌다. */

  'editor.reject.no-visible-change': '이건 눈에 안 띈다',
  'editor.reject.cell-occupied': '자리가 벌써 찼다',
  'editor.reject.out-of-bounds': '방 밖이다',
  'editor.reject.placement-limit': '놓을 수 있는 만큼 다 놨다',
  'editor.reject.verb-not-allowed': '이번 판에서는 못 하는 것',
  'editor.reject.unknown-catalog': '없는 소품이다',
  'editor.reject.unknown-instance': '벌써 사라진 물건이다',
  'editor.reject.already-changed': '한 물건은 한 번만',

  /* --- 조작 안내 ------------------------------------------------------------ */

  'controls.key.tap': '탭',
  'controls.key.tapAgain': '한 번 더 탭',
  'controls.key.drag': '드래그',
  'controls.key.wheel': '휠 또는 핀치',
  'controls.mode': '보기와 놓기 전환',
  'controls.tap': '쏘기, 또는 자리 정하기',
  'controls.confirm': '정한 자리에 확정',
  'controls.drag': '시점 돌리기',
  'controls.zoom': '줌',
  'controls.turnView': '시점 90도',
  'controls.rotateProp': '소품 돌리기. 보기 모드에서는 재장전',
  'controls.nextColor': '다음 색',
  'controls.removeProp': '치우기',
  'controls.freeHeight': '공중에 띄우기',
  'controls.heightAdjust': '높이 조절',
  'controls.quickPick': '소품 바로 고르기',
  'controls.deselect': '선택 풀기',

  /* --- 등급 (봉투) ------------------------------------------------------------ */
  /*
   * 키는 `game/economy/envelopeTable.ts`의 `Grade` 값 그대로다. `t('grade.' + grade)`.
   * GLOSSARY 1.5 - **두 언어가 같은 문자열이다.** 라틴 약어라 번역할 것이 없고, 번역하는 순간
   * 한국어 화면과 영어 화면이 같은 등급을 다른 이름으로 부른다. 확률 숫자는 여기 없다 -
   * 문자열은 `gradeOddsLabel`이 표에서 만든다(손으로 적으면 표를 고친 날 화면이 거짓말을 한다).
   *
   * 등급 이름은 조사 판정(`{item:이/가}`)이 되지 않는다. 뒤에 조사가 붙는 문장을 쓰지 마라 -
   * 아래 피티 문구가 전부 '다음 장은 SR 이상'처럼 조사 없는 형태인 이유다.
   */

  'grade.common': 'N',
  'grade.uncommon': 'R',
  'grade.rare': 'SR',
  'grade.legend': 'SSR',

  /* --- 준비물 ---------------------------------------------------------------- */
  /*
   * 키는 `game/economy/kits.ts`의 `KitDef.nameKey`·`descKey`가 가리킨다. 설명의 숫자(15초·10초·30초·2발)는
   * `KITS`의 보너스 값과 같아야 한다 - `level.8.hint`의 "5초"와 같은 결정이다(GLOSSARY 5절): 값으로 빼면
   * 호출부가 넘기는 것을 잊었을 때 `{seconds}`가 화면에 새 나간다. `index.test.ts`가 두 값을 대조한다.
   */

  'kit.magnifier.name': '돋보기',
  'kit.magnifier.desc': '외우기가 15초 길어진다',
  'kit.leisure.name': '느긋함',
  'kit.leisure.desc': '원래 방에 10초 더 있을 수 있다',
  'kit.slowClock.name': '느린 시계',
  'kit.slowClock.desc': '수색이 30초 길어진다',
  'kit.spare.name': '여분',
  'kit.spare.desc': '탄환 2발을 더 들고 들어간다',

  /*
   * 3택1 화면(`ui/kitPick.ts`, 로드맵 M9). 고르는 것은 **가진 것 중에서**이므로 잠긴 카드도 값도 없다.
   * 효과 한 줄(`kit.effect.*`)의 키 뒷토막은 `Tuning`의 필드 이름 그대로다 - `KitTuning`이 허락한 넷뿐이라
   * 새 효과 줄을 쓰려면 그 타입부터 넓혀야 한다(`economy/kits.ts` 머리말).
   */
  'kit.slotLabel': '준비물',
  'kit.pickHint': '하나만 챙긴다, 이 단계에 있는 동안 쓴다',
  'kit.pickAria': '{name} 챙기기. {desc}',
  'kit.have': '{count}개 있음',
  'kit.picked': '챙겼다',
  'kit.pickedName': '{name:을/를} 챙겼다',
  'kit.none': '빈손으로 간다',
  'kit.empty': '이번엔 챙길 것이 없다',
  'kit.emptyHint': '한 단계를 깰 때마다 하나씩 생긴다',
  'kit.effect.watchSeconds': '외우기 +{count}초',
  'kit.effect.huntSeconds': '수색 +{count}초',
  'kit.effect.originStaySeconds': '원래 방 +{count}초',
  'kit.effect.startAmmo': '탄환 +{count}발',

  /* --- 매일의 방 (로드맵 M9) ---------------------------------------------------- */
  /*
   * 하루 한 번 모두가 같은 방을 받는다. 날짜 자체(`2026-09-06`)는 숫자라 사전에 없다 - 화면이 그대로 적는다.
   * '챌린지'·'데일리'를 쓰지 않는 이유는 GLOSSARY 5절의 다른 줄과 같다(다른 게임의 말은 들이지 않는다).
   */
  'daily.eyebrow': '오늘 하루',
  'daily.title': '매일의 방',
  'daily.desc': '오늘은 모두 같은 방이다. 준비물 없이 들어가고 기록은 첫 판만 남는다',
  'daily.start': '들어가기',
  'daily.again': '한 번 더',
  'daily.streak': '연속 {count}일',
  'daily.streakNone': '연속 기록 없음',
  'daily.todayNone': '오늘 것은 아직 안 했다',
  'daily.todayDone': '오늘 {score}점, {total}개 중 {found}개',
  'daily.todayDoneScore': '오늘 {score}점',
  'daily.practice': '오늘 것은 끝났다. 다음 판은 기록에 남지 않는다',
  'daily.cardAria': '매일의 방. {status}',

  /* --- 꾸밈 ------------------------------------------------------------------ */
  /* 키는 `game/economy/looks.ts`의 `nameKey`가 가리킨다. 자리(표식·이름표·스탬프·프레임·봉투)마다 기본 하나가 있다. */

  'look.markerPin.name': '핀',
  'look.markerCircle.name': '동그라미',
  'look.markerCross.name': '가위표',
  'look.markerTriangle.name': '세모',
  'look.markerStar.name': '별',
  'look.markerSquare.name': "네모",
  'look.markerBlank.name': "빈칸",
  'look.markerNestedSquare.name': "겹네모",
  'look.markerRuled.name': "가로줄",
  'look.markerLattice.name': "창살",
  'look.markerHatch.name': "빗금",
  'look.markerShade.name': "반쪽 네모",
  'look.markerDogEar.name': "접힌 귀",
  'look.markerPunched.name': "구멍",
  'look.markerCornerFill.name': "모서리",
  'look.markerDiamond.name': "마름모",
  'look.markerNestedDiamond.name': "겹마름모",
  'look.markerBullseye.name': "과녁",
  'look.markerRing.name': "빈 동그라미",
  'look.markerHalfMoon.name': "반달",
  'look.markerMissingPiece.name': "빠진 조각",
  'look.markerWedge.name': "쐐기",
  'look.markerCone.name': "고깔",
  'look.markerStarSix.name': "여섯모 별",
  'look.markerStarEight.name': "여덟모 별",
  'look.markerStarSolid.name': "묵직한 별",
  'look.markerStarTwelve.name': "열두모 별",
  'look.markerPinwheel.name': "바람개비 핀",
  'look.markerStarRinged.name': "고리 별",
  'look.markerStarTeardrop.name': "물방울 별",
  'look.markerStarHollow.name': "속 빈 별",
  'look.markerStarPlump.name': "통통한 별",
  'look.markerStarBead.name': "구슬 별",
  'look.markerWhirl.name': "휘어진 별",
  'look.markerBlade.name': "날개 핀",
  'look.markerFlowerSix.name': "여섯잎 꽃",
  'look.markerFlower.name': "꽃",
  'look.markerFlowerWhite.name': "흰 꽃",
  'look.markerFlowerEight.name': "여덟잎 꽃",
  'look.markerSnowflake.name': "눈꽃",
  'look.markerFrostFlake.name': "함박눈",
  'look.markerArrowUp.name': "위 화살표",
  'look.markerArrowDown.name': "아래 화살표",
  'look.markerArrowRewind.name': "되감는 화살표",
  'look.markerArrowSwap.name': "엇갈린 화살표",
  'look.markerArrowDouble.name': "두 줄 화살표",
  'look.markerInfinity.name': "무한 기호",
  'look.markerThreeBars.name': "줄 세 개",
  'look.markerThreeDots.name': "점 세 개",
  'look.markerThickPlus.name': "굵은 십자",
  'look.markerFullBlock.name': "귀퉁이 칸",
  'look.markerHalfBlock.name': "반 나눈 칸",
  'look.markerShadeBlock.name': "잔무늬 칸",
  'look.markerPulse.name': '두근거리는 핀',
  'look.markerSpin.name': '빙글 도는 핀',
  'look.markerCog.name': "톱니 핀",
  'look.markerWheel.name': "바퀴 핀",
  'look.markerCompass.name': "나침반 핀",
  'look.markerRipple.name': "번지는 핀",
  'look.markerBeacon.name': "터지는 핀",
  'look.markerFloat.name': "떠오르는 핀",
  'look.tagYellow.name': '노란 이름표',
  'look.tagMint.name': '민트 이름표',
  'look.tagCoral.name': '코랄 이름표',
  'look.tagSky.name': '하늘색 이름표',
  'look.tagLilac.name': '라일락 이름표',
  'look.tagRose.name': "장미 이름표",
  'look.tagStrawberry.name': "딸기 이름표",
  'look.tagCherry.name': "앵두 이름표",
  'look.tagPeach.name': "복숭아 이름표",
  'look.tagApricot.name': "살구 이름표",
  'look.tagCopper.name': "구리 이름표",
  'look.tagIvory.name': "상아 이름표",
  'look.tagChartreuse.name': "연두 이름표",
  'look.tagMugwort.name': "쑥 이름표",
  'look.tagSprout.name': "새싹 이름표",
  'look.tagMoss.name': "이끼 이름표",
  'look.tagGrass.name': "잔디 이름표",
  'look.tagPea.name': "완두 이름표",
  'look.tagGreen.name': "초록 이름표",
  'look.tagEmerald.name': "에메랄드 이름표",
  'look.tagCeladon.name': "청자색 이름표",
  'look.tagJade.name': "옥색 이름표",
  'look.tagTeal.name': "청록 이름표",
  'look.tagAqua.name': "물빛 이름표",
  'look.tagOcean.name': "바다 이름표",
  'look.tagAsh.name': "잿빛 이름표",
  'look.tagSilver.name': "은색 이름표",
  'look.tagIndigo.name': "쪽빛 이름표",
  'look.tagCornflower.name': "수레국화 이름표",
  'look.tagNavy.name': "청보라 이름표",
  'look.tagHydrangea.name': "수국 이름표",
  'look.tagIris.name': "붓꽃 이름표",
  'look.tagWisteria.name': "등나무 이름표",
  'look.tagViolet.name': "보라 이름표",
  'look.tagGrape.name': "포도 이름표",
  'look.tagAmethyst.name': "자수정 이름표",
  'look.tagPlum.name': "자두 이름표",
  'look.tagPeony.name': "모란 이름표",
  'look.tagRouge.name': "연지 이름표",
  'look.tagFuchsia.name': "진분홍 이름표",
  'look.tagBlossom.name': "벚꽃 이름표",
  'look.stampBasic.name': '기본 스탬프',
  'look.stampDetective.name': '탐정 스탬프',
  'look.stampDeadpan.name': '무뚝뚝한 스탬프',
  'look.stampLoud.name': '요란한 스탬프',
  'look.stampRookie.name': "신입 스탬프",
  'look.stampClerk.name': "사무원 스탬프",
  'look.stampGuard.name': "경비원 스탬프",
  'look.stampCourier.name': "택배 기사 스탬프",
  'look.stampTeacher.name': "선생님 스탬프",
  'look.stampBoss.name': "사장님 스탬프",
  'look.stampReporter.name': "기자 스탬프",
  'look.stampCleaner.name': "청소부 스탬프",
  'look.stampLibrarian.name': "사서 스탬프",
  'look.stampScared.name': "겁먹은 스탬프",
  'look.stampPolite.name': "예의 바른 스탬프",
  'look.stampSleepy.name': "졸린 스탬프",
  'look.stampHasty.name': "성급한 스탬프",
  'look.stampKind.name': "다정한 스탬프",
  'look.stampBored.name': "심드렁한 스탬프",
  'look.stampFlustered.name': "호들갑 스탬프",
  'look.stampShy.name': "수줍은 스탬프",
  'look.stampGrumpy.name': "화난 스탬프",
  'look.stampPlayByPlay.name': "중계 스탬프",
  'look.stampBreakingNews.name': "속보 스탬프",
  'look.stampAd.name': "광고 스탬프",
  'look.stampAnnouncement.name': "안내 방송 스탬프",
  'look.stampPoem.name': "시 스탬프",
  'look.stampFolkTale.name': "옛날이야기 스탬프",
  'look.stampManual.name': "설명서 스탬프",
  'look.stampDiary.name': "일기 스탬프",
  'look.stampMachine.name': "기계 스탬프",
  'look.frameNone.name': '테두리 없음',
  'look.frameTape.name': '마스킹테이프 테두리',
  'look.frameClip.name': '클립보드 테두리',
  'look.frameLabel.name': "라벨 테두리",
  'look.frameSealTape.name': "포스트잇 테두리",
  'look.frameTearLine.name': "절취선 테두리",
  'look.framePushpin.name': "압정 테두리",
  'look.frameManuscript.name': "원고지 테두리",
  'look.frameFold.name': "접힌 종이 테두리",
  'look.frameGlass.name': "유리판 테두리",
  'look.frameIndexTab.name': "책갈피 테두리",
  'look.framePencil.name': "연필선 테두리",
  'look.frameFolder.name': "서류철 테두리",
  'look.frameRuler.name': "자 테두리",
  'look.frameFilm.name': "필름 테두리",
  'look.frameSpine.name': "책등 테두리",
  'look.frameTwine.name': "노끈 테두리",
  'look.frameDoorJamb.name': "문틀 테두리",
  'look.frameWindowSill.name': "창틀 테두리",
  'look.frameGrid.name': "모눈종이 테두리",
  'look.frameMatBoard.name': "마분지 테두리",
  'look.envelopeKraft.name': '크라프트 봉투',
  'look.envelopeStriped.name': '줄무늬 봉투',
  'look.envelopeStamped.name': '도장 찍힌 봉투',
  'look.envelopeGilt.name': '금박 봉투',
  'look.envelopeGrid.name': "모눈 봉투",
  'look.envelopeLattice.name': "굵은 격자 봉투",
  'look.envelopePinstripe.name': "가는 세로줄 봉투",
  'look.envelopeHatch.name': "빗금 봉투",
  'look.envelopeDiamond.name': "마름모 봉투",
  'look.envelopeCheck.name': "체크무늬 봉투",
  'look.envelopeRuled.name': "줄친 봉투",
  'look.envelopeDoubleRule.name': "이중선 봉투",
  'look.envelopeRipple.name': "물결 봉투",
  'look.envelopeSpeckle.name': "점박이 봉투",
  'look.envelopeWoodGrain.name': "나무결 봉투",
  'look.envelopeCorrugated.name': "골판지 봉투",
  'look.envelopeWideBand.name': "굵은 띠 봉투",
  'look.envelopeHairline.name': "가는 띠 봉투",
  'look.envelopeAir.name': "항공 봉투",
  'look.envelopeWindow.name': "창문 봉투",
  'look.envelopePostmark.name': "우표 붙은 봉투",
  'look.envelopePerf.name': "톱니 봉투",
  'look.envelopeCrease.name': "접힌 자국 봉투",
  'look.envelopeDoubleSeal.name': "두 번 봉한 봉투",
  'look.envelopeSilver.name': "은박 봉투",
  'look.envelopeCopper.name': "구리 봉투",
  'look.envelopeStain.name': "얼룩 봉투",
  'look.envelopeSunburst.name': "빛살 봉투",
  'look.envelopeDiagonal.name': "사선 봉투",
  'look.envelopeDogEar.name': "접은 모서리 봉투",
  'look.envelopeRegistered.name': "등기 봉투",
  /* 완주 보상 다섯. 봉투에서 나오지 않고 다 모은 사람에게만 간다(`looks.ts`의 `source: 'complete'`). */
  'look.markerGlint.name': '반짝이는 핀',
  'look.tagPink.name': '분홍 이름표',
  'look.stampProud.name': '뻐기는 스탬프',
  'look.frameGallery.name': '액자 테두리',
  'look.envelopeWax.name': '밀랍 봉투',

  /* --- 별명 ------------------------------------------------------------------ */
  /*
   * 키는 `game/economy/titles.ts`의 `nameKey`가 가리킨다. GLOSSARY 1.5 - '타이틀'이 아니라 '별명'이다.
   * 톤은 가벼운 탐정물이다. 수사·범죄 어휘는 없다.
   */

  'nickname.emptySeatSpotter': '빈 자리 감별사',
  'nickname.doorRegular': '문 앞 단골',
  'nickname.chairCounter': '의자 세는 사람',
  'nickname.cornerWatcher': '구석 지킴이',
  'nickname.firstShotFirst': '첫 발부터',
  'nickname.lastSecondFinder': '막판에 찾는 사람',
  'nickname.turnedThingsNemesis': '돌아앉은 것의 천적',
  'nickname.colorHawk': '색 매의 눈',
  'nickname.noMissNoFuss': '한 발도 안 틀린',
  'nickname.quietObserver': '말없이 보는 사람',
  'nickname.visitor': '다녀간 사람',
  'nickname.sameRouteEveryTime': "늘 같은 순서",
  'nickname.alwaysClockwise': "시계 방향으로만",
  'nickname.oneLapOnArrival': "들어가면 한 바퀴",
  'nickname.windowsFirst': "창문부터 보는",
  'nickname.looksUpToo': "천장까지 보는",
  'nickname.eyesOnTheFloor': "바닥만 훑는",
  'nickname.behindTheFurniture': "가구 뒤부터",
  'nickname.tiltsTheHeadFirst': "고개부터 기울이는",
  'nickname.rightUpClose': "코앞까지 가는",
  'nickname.takesAStepBack': "한 걸음 물러서는",
  'nickname.holdsABreath': "숨 참고 보는",
  'nickname.doubtsItFirst': "일단 의심부터",
  'nickname.staresAtWhatStayed': "안 바뀐 걸 보는",
  'nickname.sameSpotTwice': "같은 자리 또 보는",
  'nickname.countsTheShotsFirst': "탄환 먼저 세는",
  'nickname.alwaysOneLeft': "늘 한 발 남는",
  'nickname.onlyWhenSure': "확실할 때만",
  'nickname.allAtOnce': "몰아서 쏘는",
  'nickname.shootAndMoveOn': "쏘고 안 보는",
  'nickname.threeThenABreather': "셋 찾고 한숨 돌리는",
  'nickname.keepsLookingAnyway': "다 찾고도 더 보는",
  'nickname.originalRoomAgain': "원래 방에 또 가는",
  'nickname.usesEverySecond': "시간 다 쓰는 사람",
  'nickname.staysForTheResults': "결과를 오래 보는",
  'nickname.straightBackIn': "바로 다시 하는",
  'nickname.framesLevel': "액자 각 담당",
  'nickname.plantFriend': "화분의 친구",
  'nickname.lampOnly': "스탠드만 보는 눈",
  'nickname.drawerGap': "서랍 한 칸 차이",
  'nickname.rugCorner': "러그 모서리 담당",
  'nickname.clockHands': "시계 바늘부터",
  'nickname.cupHandle': "컵 손잡이 방향",
  'nickname.cushionSquare': "쿠션 각 맞추기",
  'nickname.bookSpines': "책등부터",
  'nickname.windowLatch': "창문 걸쇠부터",
  'nickname.doorknobEye': "문고리부터",
  'nickname.outletCount': "콘센트 위치까지",
  'nickname.bulbDuty': "전등 담당",
  'nickname.mirrorToo': "거울도 보는 사람",
  'nickname.chairAngle': "의자 각도까지",
  'nickname.shelfLine': "선반 줄 재는 눈",
  'nickname.curtainFolds': "커튼 주름 담당",
  'nickname.switchWay': "스위치 방향까지",
  'nickname.calendarPage': "달력 한 장",
  'nickname.slipperPair': "슬리퍼 짝 맞추기",
  'nickname.trashBin': "휴지통도 본다",
  'nickname.remoteSpot': "리모컨 자리",
  'nickname.keysThere': "열쇠는 늘 거기",
  'nickname.wallpaperPattern': "벽지 무늬까지",
  'nickname.pencilOne': "연필 한 자루도",
  'nickname.lastOneIn': "마지막에 들어온",
  'nickname.inNoHurry': "산책하듯 걷는",
  'nickname.skipsTheBriefing': "안내는 건너뛰는",
  'nickname.cameOutEmptyHanded': "빈손으로 나온 날",
  'nickname.justWarmingUp': "이번 판은 몸풀기",
  'nickname.nextRoundForReal': "다음 판이 진짜",
  'nickname.theGuessLanded': "찍은 게 맞은 날",
  'nickname.justAFeeling': "그냥 느낌으로",
  'nickname.luckyKindOfDay': "오늘 운이 좋은",
  'nickname.rightBehindYou': "늘 반 박자 뒤",
  'nickname.trustsTheNeighbor': "옆 사람 믿는",
  'nickname.gaspedAlong': "같이 놀란 사람",
  'nickname.hereForTheRoom': "외우기가 본업",
  'nickname.likesThePlace': "방이 마음에 든",
  'nickname.intoTheWallpaper': "벽지에 빠진",
  'nickname.complimentsTheLighting': "조명 칭찬하는",
  'nickname.triesTheChair': "의자에 앉아 보는",
  'nickname.wantsToTidyUp': "정리부터 하고 싶은",
  'nickname.wouldLiveHere': "여기 살고 싶은",
  'nickname.bigReactions': "반응이 큰 사람",
  'nickname.clapsTheLoudest': "박수 소리가 큰",
  'nickname.thinkingOutLoud': "혼잣말이 많은 사람",
  'nickname.laughedAndMissed': "웃다가 놓친 사람",
  'nickname.lostTrackOfTime': "시간 가는 줄 모른",
  'nickname.eyesFirst': "눈이 먼저 간다",
  'nickname.quickHands': "손이 빠른 사람",
  'nickname.fasterThanClock': "시계보다 빠른",
  'nickname.allInOneGo': "단숨에 끝내는",
  'nickname.rightEveryTime': "매번 맞히는 사람",
  'nickname.noWastedMove': "헛손질 없는",
  'nickname.fastAndSure': "빠르고 정확한",
  'nickname.firstGuessRight': "첫 짐작이 맞는",
  'nickname.steadyHand': "떨리지 않는 손",
  'nickname.neverRattled': "흔들리지 않는",
  'nickname.inNoRush': "서두르지 않는",
  'nickname.neverBlinks': "깜빡이지 않는",
  'nickname.oneAfterAnother': "연달아 맞히는",
  'nickname.unbrokenRun': "끊기지 않는",
  'nickname.alwaysInForm': "늘 같은 솜씨",
  'nickname.notAScratch': "흠 하나 없이",
  'nickname.cleanRecord': "전적이 깨끗한",
  'nickname.cleanFinish': "깔끔하게 끝내는",
  'nickname.comesFromBehind': "뒤늦게 치고 오는",
  'nickname.turnsItAround': "판을 뒤집는",
  'nickname.oneForTheRecord': "오래 남을 한 판",
  'nickname.goodInstincts': "감이 좋은 사람",
  'nickname.knowsWhereToLook': "어디 볼지 아는",
  'nickname.seesItThrough': "끝까지 보는 사람",
  'nickname.patientEye': "느긋한 눈",
  /* 완주 보상. 하나도 안 빠뜨렸다는 말이 곧 이름이다. */
  'nickname.sawItAll': '빠짐없이',

  /*
   * --- 여럿이 (로드맵 M2 - 봇과 한 판) ------------------------------------------------
   *
   * 매치 세션(`game/matchSession.ts`)이 띄우는 자막. 남의 발견은 이름표(이름 그대로, 번역 없음)라 여기 없다.
   * `match.foundBy`는 "OO이 먼저 찾았다"(로드맵 2.6절 `sniped` - 손해 0인 피드백)이고, 조사는 받침 있는 쪽이 앞이다.
   * 참가자 줄(`ui/playerStrip.ts`)·순위표(`ui/rankingList.ts`)·"여럿이" 카드(`ui/levelSelect.ts`)의 문구도 여기다.
   * 어휘는 GLOSSARY 1.5절 - 여럿이 · 찾는 사람 · 다녀간 사람 · 관전 · 줄. '봇'은 코드의 말이고 화면에서는 '이웃'이다.
   */
  'match.foundBy': '{name:이/가} 먼저 찾았다',
  'match.spectating': '탄환이 다 떨어졌다. 이제 관전이다',
  'match.doorOccupied': '원래 방에 누가 있다. 줄 {position}번째',
  'match.doorYourTurn': '원래 방 차례다. {seconds}초 안에 눌러라',
  /* 결과 화면의 출구. 단계가 아니라 판에서 나가는 것이라 '단계 고르기'(`result.exit`)와 말이 다르다. */
  'match.leave': '나가기',
  /* 결과 화면 머리 태그. 여럿이는 시도 번호가 없고 라운드가 있다(`result.attempt`의 자리). */
  'match.round': '{round}라운드',
  /* 걸기(M7) 배너. 누가 걸었는지가 전원에게 1초 - 컨셉시트 5절의 말싸움을 화면이 대신한다. 조사는 받침 있는 쪽이 앞이다. */
  'wager.banner': '{name:이/가} 걸었다!',
  /** 내가 건 배너. 이름 자리에 '나'(`party.you`)를 넣으면 '나가 걸었다!'가 되므로 문장을 간다. */
  'wager.banner.self': '내가 걸었다!',
  /* 봇 판의 좌석 이름. 사람은 '나', 봇은 이웃 - 방을 바꾸고 다니는 것은 이웃이지 적이 아니다(가벼운 탐정 톤). */
  'party.you': '나',
  'party.botName': '이웃 {n}',
  /*
   * 단계 선택 화면 하단의 "여럿이" 카드. `select.footGraduated`("이제 친구를 부르면 된다")가 가리키는 카드다.
   * M6에서 이 카드가 여는 것이 봇 판이 아니라 **로비**가 되면서 문구도 방을 말한다 - 이웃 수·난이도는 카드가 약속할 수
   * 있는 값이 아니게 됐다(`ui/levelSelect.LevelSelectParty`). 난이도 이름(`party.level.*`)은 주소창 도구(`?botLevel=`)의
   * 말로 남는다 - 숫자(N, p, q)가 아니라 이웃의 성격으로 말한다. 화면에 확률을 적지 않는다.
   */
  'party.card.eyebrow': '여럿이',
  'party.card.title': '친구와 한 판',
  'party.card.desc': '방을 만들고 번호를 알려 주면 같은 방을 함께 수색한다. 빈 자리는 이웃이 채운다',
  'party.card.start': '들어가기',
  'party.card.chipSeats': '{max}명까지',
  'party.card.aria': '여럿이. {title}. {desc}',
  'party.hub.publicTag': '공개',
  'party.hub.publicTitle': '아무나 한 판',
  'party.hub.publicDesc': '지금 열려 있는 방에 들어간다. 없으면 하나 열면 된다',
  'party.browse.title': '열린 방',
  'party.browse.seats': '{players}/{max}',
  'party.browse.chipOddities': '인당 {count}',
  'party.browse.rowAria': '{host}의 방. {seats}',
  'party.browse.create': '새 방 열기',
  'party.browse.refresh': '다시 보기',
  'party.browse.loading': '열린 방을 찾는 중',
  'party.browse.empty': '지금 열린 방이 없다. 하나 열면 여기 뜬다',
  'lobby.visibility.public': '공개 방',
  'lobby.visibility.private': '비공개 방',
  'lobby.visibility.aria': '지금 {state}. 눌러서 바꾸기',
  'party.hub.privateTag': '비공개',
  'party.hub.rankedTag': '준비중',
  'party.hub.rankedTitle': '겨루기',
  'party.hub.rankedDesc': '무작위 상대와 순위를 다툰다',
  'party.hub.rankedLocked': '계정이 생기면',
  'party.level.easy': '느긋한 이웃',
  'party.level.normal': '보통 이웃',
  'party.level.hard': '눈 밝은 이웃',
  /* 참가자 줄. 화면 위에 좌석마다 한 칸 - 이름 · 찾은 수 · (내 칸만) 탄환 점 · 표식 셋(관전 · 원래 방 · 다녀간 사람). */
  'party.strip.aria': '참가자 줄',
  'party.strip.found': '{count} 찾음',
  'party.strip.rowAria': '{name}. 이상한 곳 {found}개 찾음',
  'party.visitor': '다녀간 사람',
  'party.inOrigin': '원래 방에',

  /*
   * --- 구획 (로드맵 M11 - 각자 자기 구획을 고치고 남의 구획을 찾는다) --------------------
   *
   * '구획'은 GLOSSARY 1.5의 고정어다. **'방'이라 부르지 않는다** - 이 게임에는 이미 '원래 방'이 있고,
   * 한 화면에서 '방'이 두 가지를 가리키면 "원래 방으로 가라"가 무슨 말인지 알 수 없게 된다.
   * 구획의 **이름**은 여기 없다 - 평면도가 준 id로 `room.<id>`를 조회한다(`i18n/zoneName.ts`가 그 접두사의 원본).
   */
  /** 바꾸기 진입 자막. 60초 동안 손댈 수 있는 곳이 어디인지가 첫 문장이어야 한다. */
  'zone.assigned': '{name:이/가} 네 구획이다',
  /** 구획을 못 받은 판(좌석보다 구획이 적다 - 있으면 안 되는 자리라 방어 문구다). */
  'zone.none': '이번 판에는 손댈 구획이 없다',
  /** 남의 구획 물건을 탭했을 때. 잠금이 이미 막고 있으므로 한 줄로 끝낸다(GLOSSARY 3절 - 왜 안 되는지는 말한다). */
  'zone.notMine': '내 구획이 아니다',
  /** 바꾸기 띠·HUD 칩의 이름표. 이름 앞의 두 글자가 "지금 이 이름은 내 것"을 말한다. */
  'zone.mine': '내 구획',
  'zone.mineAria': '내 구획 {name}',
  /*
   * 바꾸기 중 남들의 진행판(`ui/zoneProgress.ts`). 전원이 동시에 자기 구획을 고치므로 이 화면은 '기다림'이 아니라
   * **진행**이다 - 이름과 예산 점만 있고 무엇을 건드렸는지는 뷰에 자리가 없다.
   */
  'zone.progress.title': '다들 손대는 중',
  'zone.progress.aria': '구획 진행',
  'zone.progress.rowAria': '{name}. 이상한 곳 {used}개, {budget}개까지',
  'zone.progress.doneAria': '{name}. 다 했다',
  'zone.progress.hint': '아직 손대기 전이다. 더 외워 두라',
  /*
   * 리빌 이름표 아래 한 줄. 누구 구획이었는지가 붙어야 "아무도 못 봤다"가 **주인의 점수**로 읽힌다(M11 규칙 5).
   * 소유격 '의'는 받침을 가리지 않으므로 조사 표기를 쓰지 않는다.
   */
  'zone.reveal.owner': '{name}의 구획',
  'zone.reveal.ownerMine': '내 구획',
  'zone.reveal.nobodyOwner': '{name}의 점수가 된다',
  'zone.reveal.nobodyMine': '내 점수가 된다',
  /** 결과 화면 타일 · 순위표 내 줄. 점수의 근거를 숫자 하나로 말한다. */
  'zone.result.label': '내 구획 미발견',
  'zone.result.value': '{count}개',
  'zone.result.rowAria': '내 구획에서 아무도 못 찾은 것 {count}개',
  /** 로비 좌석 수 옆 한 줄. 인원이 곧 구획 수라는 것이 방을 만들기 전에 보여야 한다. */
  'zone.lobby.note': '구획은 인원 수만큼 생긴다. 각자 자기 구획을 고친다',

  /*
   * 관전(GLOSSARY 1.5 - 탈락·죽음·아웃이 아니다). 탄환이 다 떨어진 사람은 방을 계속 보되 쏘지 못한다.
   * 쏘려 하면 왜 안 되는지를 그 자리에 말한다(3절 - 눌리지 않는 버튼은 고장과 구별되지 않는다).
   */
  'spectate.badge': '관전',
  'spectate.rowAria': '{name}. 관전 중',
  'spectate.shotBlocked': '관전 중이라 못 쏜다',
  /*
   * 내 구획의 이상한 곳을 쏘면(M11 규칙 4 - 그 구획을 다녀간 사람이 나다). 손해는 0이지만 말 없는 표식은 고장으로 읽힌다.
   * M4에서는 "내가 체인저인 라운드"의 문구였고, 역할이 사라진 뒤 같은 뜻이 구획 하나로 좁아졌다.
   */
  'party.visitorShotBlocked': '다녀간 사람은 쏘지 않는다',
  'spectate.watching': '{name:을/를} 보는 중',
  /*
   * 시점 줄(`ui/spectateBar.ts`, M5). 관전 중에만 서는 "OO 시점" 버튼과 그 사람의 최근 발견 티커. 물건 이름은 카탈로그의
   * 것이라 여기 없다(`reveal.ts`와 같은 결정). 조사는 받침 있는 쪽이 앞이다.
   */
  'spectate.bar.aria': '시점 고르기',
  'spectate.target': '{name} 시점',
  'spectate.ticker.found': '{name:이/가} {item:을/를} 찾았다',
  /*
   * 문 앞 줄(`ui/doorQueue.ts`, M5). 원래 방 버튼 옆 - 보유자("OO 나가기 7")·차례·줄은 전원에게 같은 값이다(규칙 5).
   * 초는 올림이고 단위를 붙이지 않는다 - 원래 방 버튼의 "나가기 {seconds}"(`hud.origin.staying.label`)와 같은 얼굴이어야 한다.
   */
  'door.aria': '원래 방 문 앞',
  'door.holder': '{name} 나가기 {seconds}',
  /** 보유자가 나일 때. '나 나가기 7'이 되지 않게 이름 없는 문장이다(칩의 `is-me`가 내 것임을 그린다). */
  'door.holderMe': '내가 안에 있다. 나가기 {seconds}',
  'door.queueAria': '줄 {count}명',
  'door.queuedAria': '{name}. 줄 {position}번째',
  'door.yourTurn': '내 차례 {seconds}',
  'door.reservedFor': '{name} 차례',

  /*
   * --- 바꾸기 도구 (로드맵 M4 - 사람 체인저) ------------------------------------------------------
   *
   * 내가 다녀간 사람인 라운드의 60초. 하단 띠(`ui/changerBar.ts`)의 말이고, 동사 버튼의 이름은 `verb.*`를 그대로 쓴다
   * (단계 안내 화면·결과 화면과 같은 어휘 - 여기서 "삭제"·"이동"이라 부르면 같은 것이 세 이름을 갖는다).
   * 잠긴 버튼의 이유는 `editor.reject.*`(verbs가 말한 것) 또는 `changer.lock.*`(예산 · 고른 물건 없음)이다.
   * 화면에 '체인저'·'예산'·'동사'는 나오지 않는다(GLOSSARY 1.5 · 2절).
   */
  'changer.barAria': '바꾸기 도구',
  'changer.budgetAria': '이상한 곳 {used}개 만들었다. {budget}개까지',
  'changer.undo': '되돌리기',
  'changer.undoAria': '{name:을/를} 되돌린다',
  'changer.done': '다 했다',
  'changer.doneAria': '다 했다. 남은 {left}개는 저절로 채워진다',
  'changer.hint.idle': '바꿀 물건을 탭하라. 새로 놓으려면 생김',
  'changer.hint.selected': '{name}. 어떻게 바꿀지 고르라',
  'changer.hint.moving': '{name:을/를} 옮길 자리를 탭하라',
  'changer.hint.adding': '{name:을/를} 놓을 자리를 탭하라',
  'changer.lockedAria': '{verb}. {reason}',
  'changer.lock.budget': '만들 수 있는 만큼 다 만들었다',
  'changer.lock.no-target': '먼저 물건을 탭하라',
  'changer.catalogAria': '놓을 소품',
  'changer.colorsAria': '바꿀 색',
  /*
   * 다녀간 사람의 걸기(M7) - "N개는 못 찾는다". [다 했다] 옆의 스테퍼다. 0이면 걸지 않은 것이고, 맞으면 점수 두 배,
   * 틀려도 깎이는 것은 없다(`raise.wagerMultiplier` - 벌은 없다). 그래서 문구가 위험을 말하지 않는다.
   */
  'changer.wager.label': '못 찾을 개수',
  'changer.wager.none': '안 건다',
  'changer.wager.count': '{count}개',
  'changer.wager.aria': '못 찾을 개수 {count}개. 맞으면 점수 두 배',
  'changer.wager.dec': '하나 줄이기',
  'changer.wager.inc': '하나 늘리기',
  'changer.wager.toggleAria': '못 찾을 개수 - {value}. 눌러서 고른다',
  'changer.doneWagerAria': '다 했다. {count}개는 못 찾는다고 걸었다',

  /*
   * 꾸미기 띠(`ui/decorateBar.ts`). 안내 한 줄은 **지금 할 일**이고 손의 상태에 따라 셋으로 갈린다 -
   * 손이 비었으면 고르라고, 들었으면 놓으라고, 다 놓았으면 무를 수 있다고 말한다. 다 쓴 사람에게 계속
   * "놓아라"라고 하면 그것이 곧 막다른 화면이다. [다 놓았다]는 여기 없다 - 그 투표 버튼은 HUD의 것이다
   * (`phase.decorate.skip`).
   */
  'decorate.left': '{count}개 더 놓을 수 있다',
  'decorate.hint.pick': '소품을 골라라',
  'decorate.hint.place': '빈 자리를 눌러 놓는다',
  'decorate.hint.spent': '다 놓았다 - 내 소품을 누르면 걷는다',

  /*
   * 여럿이 첫 바꾸기 안내(`ui/changerCoach.ts`). 읽고 넘기는 설명이 아니라 **지금 할 일 한 줄**이라,
   * 제목은 시키는 말이고 힌트는 그 방법이다. 동사 이름은 띠에 적힌 것 그대로 쓴다(`verb.*`) -
   * 여기서 "추가"라고 부르면 사람이 [생김] 버튼을 못 찾는다.
   */
  'coach.changer.place': '네 구획에 이상한 곳을 만들어라',
  'coach.changer.place.hint': '[생김] → 물건 고르기 → 놓을 자리를 끌어서 겨눈다',
  'coach.changer.edit': '놓은 것도 다시 고칠 수 있다',
  'coach.changer.edit.hint': '물건을 누르면 옮기고 돌리고 색을 바꾼다. 여러 번 고쳐도 하나다',
  'coach.changer.done': '다 만들었다',
  'coach.changer.done.hint': '[다 했다]. 안 눌러도 시간이 되면 넘어간다',
  'coach.changer.close': '알겠다',

  /*
   * 수색 진입 뒤 동사별 개수 예고(`config.revealVerbCounts`, 기본 켬). 개수는 보는 사람이 찾을 수 있는 것만이고(M11),
   * 단계 안내 화면과 같은 어휘(`verb.*`)다 - 여기서 "삭제 3"이라 부르면 같은 것이 두 이름을 갖는다. 0개인 동사는 적지 않는다.
   */
  'verbCounts.item': '{verb} {count}',
  'verbCounts.notice': '이번엔 {list}',

  /* 순위표(결과 화면). 같은 점수는 같은 등수다(`matchScore.cumulativeRanking`). 내 줄만 다르게 그린다. */
  'ranking.title': '순위',
  'ranking.round': '이번 판',
  'ranking.total': '누적',
  'ranking.rank': '{rank}등',
  'ranking.rowAria': '{rank}등 {name}, 이번 판 {round}점, 누적 {total}점',

  /* --- 핀 ------------------------------------------------------------------ */
  /*
   * 재화는 '핀'이다(GLOSSARY 1.5 - 코인·골드·젬·포인트·도장은 쓰지 않는다). 칩·결과 화면 줄·봉투 게이지가
   * 같은 말을 쓴다. 봉투 값(30)은 문구에 적지 않고 `{price}`로 받는다 - 값의 원본은 `envelopeTable.ts`다.
   */

  'pins.chip.label': '핀',
  'pins.chip.aria': '핀 {count}개',
  /** 결과 화면 점수표 마지막 줄. 굴러 오르는 값이라 부호를 붙인다(`result.score.*`의 `+240`과 같은 문법). */
  'pins.result.label': '핀',
  'pins.result.value': '+{count} 핀',
  /** 봉투 게이지. 핀이 값에 닿았는지, 아니면 몇 핀이 더 필요한지. */
  'pins.gauge.ready': '봉투를 열 수 있다',
  'pins.gauge.until': '봉투까지 {count}핀',
  'pins.gauge.aria': '핀 {pins}개, 봉투 한 장에 {price}개',

  /* --- 봉투 --------------------------------------------------------------- */
  /*
   * 뽑기는 '봉투'다(GLOSSARY 1.5 - 가챠·뽑기·상자·팩은 쓰지 않는다). 확률 숫자는 여기 없다 -
   * `gradeOddsLabel`이 표에서 만든다. 피티 문구가 'SR'을 말하는 이유: 카운터가 보장하는 것이
   * **SR 이상**이라서다(`envelope.ts`의 `forcedGrade`). 'R'이라 적으면 화면이 약속보다 낮게 말한다.
   */

  'envelope.title': '봉투',
  'envelope.open': '봉투 열기',
  'envelope.openAria': '봉투 열기. 한 장에 핀 {price}개',
  'envelope.price': '{price}핀',
  'envelope.oneMore': '한 장 더',
  'envelope.tenMore': '열 장',
  /** 핀이 모자랄 때 [한 장 더]가 말하는 이유(로드맵 고정 규칙 8). 눌리지 않는 버튼은 고장과 구별되지 않는다. */
  'envelope.notEnough': '{n}판이면 다시 연다',
  'envelope.seq': '{seq}번째 봉투',
  'envelope.replay': '{seq}번째 봉투 다시 보기',
  'envelope.skipHint': '탭하면 바로 카드',
  /** 머리 줄의 건너뛰기 버튼. 화면 아무 데나 탭해도 같은 일이 일어나지만, 그 문이 보이지 않으면 없는 것과 같다. */
  'envelope.skip': '건너뛰기',
  'envelope.cardOf': '{index} / {total}',
  'envelope.oddsTitle': '확률',
  'envelope.odds': '{grade} {odds}',
  /** 피티 게이지. 카운터가 임계에 닿으면 다음 장이 보장된다. */
  'envelope.pity.rare': 'SR까지 {count}장',
  'envelope.pity.legend': 'SSR까지 {count}장',
  'envelope.pity.rareNow': '다음 장은 SR 이상',
  'envelope.pity.legendNow': '다음 장은 SSR',
  /** 피티가 등급을 올린 회차의 스탬프. 바늘은 멎은 자리에 그대로 두고 이 말로 이유를 밝힌다. */
  'envelope.forced.rare': 'SR 보장',
  'envelope.forced.legend': 'SSR 보장',
  /** 산출물 한 줄. 종류가 곧 키의 가운데 토막이다(`envelope.reward.<kind>`). */
  'envelope.reward.magazines': '탄창 {count}개',
  'envelope.reward.pins': '핀 {count}개',
  'envelope.reward.kit': '{name} {count}개',
  'envelope.reward.look': '꾸밈, {name}',
  'envelope.reward.nickname': '별명, {name}',
  /** 가치 0인 봉투는 없다(로드맵 고정 규칙 4). 중복과 상한 초과는 핀으로 바뀌고, 그 사실을 한 줄로 말한다. */
  'envelope.duplicate': '이미 있어서 핀 {count}개로',
  'envelope.overflow': '탄창이 가득해서 핀 {count}개로',
  'envelope.magsChip': '탄창',
  'envelope.magsGain': '+{count}',

  /* --- 사물함 -------------------------------------------------------------- */
  /* 꾸밈·별명을 두는 곳은 '사물함'이다(GLOSSARY 1.5 - 인벤토리·창고는 쓰지 않는다). */

  'locker.title': '사물함',
  'locker.open': '사물함 열기',
  'locker.eyebrow': '가진 것',
  'locker.equipped': '끼운 것',
  'locker.equipAria': '{name} 끼우기',
  'locker.unequipNickname': '별명 빼기',
  'locker.nicknames': '별명',
  'locker.nicknameNone': '별명 없음',
  'locker.kits': '준비물',
  'locker.kitCount': '{count}개',
  /** 꾸밈 자리 이름. 자리가 곧 키의 마지막 토막이다(`locker.slot.<slot>`, `looks.ts`의 `LOOK_SLOTS`). */
  'locker.slot.marker': '표식',
  'locker.slot.nametag': '이름표',
  'locker.slot.stamp': '스탬프',
  'locker.slot.frame': '결과 테두리',
  'locker.slot.envelope': '봉투 겉면',
  'locker.odds': '봉투 확률',
  'locker.oddsNote': 'SR 없이 {rare}장이면 다음은 SR 이상. SSR 없이 {legend}장이면 다음은 SSR',
  'locker.recent': '최근 봉투',
  'locker.empty': '아직 봉투를 열지 않았다',
  'locker.summary': '핀 {pins}개, 봉투 {opened}장 열었다',
  /*
   * 수집률. 분모는 **봉투에서 나오는 것만**이다(`inventory.collectionOf`) - 기본 꾸밈까지 세면
   * 아무것도 안 뽑은 사람이 15%에서 시작한다. 구획 제목 옆의 짧은 쪽은 백분율 없이 수만 말한다.
   */
  'locker.collection': '수집률 {percent}% ({owned} / {total})',
  'locker.collectionOf': '{owned} / {total}',
  /*
   * 아직 못 얻은 완주 보상 한 줄. 버튼이 아니라 판이라 **이유가 보인다** - 눌리지 않는 것 옆에
   * 까닭이 없으면 사람은 그것을 고장으로 읽는다.
   */
  'locker.completionLocked': '다 모으면',
  'locker.completionNote': '봉투에서 나오는 것을 다 모으면 자리마다 하나씩 열린다',

  /*
   * --- 로비 (로드맵 M6 - 방 번호로 모인다) ----------------------------------------
   *
   * 여럿이 방의 대기실(`ui/lobby.ts`). 두 얼굴이다 - 방을 만들거나 번호로 들어가는 폼, 그리고 방 안(번호 크게 · 참가자 · 준비 · 시작).
   * 어휘는 GLOSSARY 1.5절 그대로다 - 봇은 '이웃', 방을 바꾸는 사람은 '다녀간 사람'. '방 번호'는 서버가 발급한 네 글자다(GLOSSARY 1.4의
   * 소품 규칙처럼 '코드'·'ID'라 부르지 않는다 - 전화로 불러 주는 물건이라 '번호'가 사람의 말이다).
   */
  'lobby.eyebrow': '여럿이',
  'lobby.form.title': '방을 만들거나 들어간다',
  'lobby.name.label': '이름',
  'lobby.name.default': '탐정',
  'lobby.create': '방 만들기',
  'lobby.code.label': '방 번호',
  'lobby.join': '번호로 들어가기',
  'lobby.back': '뒤로',
  'lobby.connecting': '방에 들어가는 중',
  'lobby.codeAria': '방 번호 {code}',
  'lobby.share': '이 번호를 알려 주면 같은 방에 들어온다',
  'lobby.seats': '{count}/{max}명',
  'lobby.seatsAria': '참가자 {count}명. 최대 {max}명',
  'lobby.tag.host': '방장',
  'lobby.tag.neighbor': '이웃',
  'lobby.tag.ready': '준비됨',
  'lobby.tag.waiting': '기다리는 중',
  'lobby.tag.away': '끊김',
  'lobby.addNeighbor': '이웃 넣기',
  'lobby.removeNeighbor': '이웃 빼기',
  'lobby.ready': '준비',
  'lobby.unready': '준비 취소',
  'lobby.start': '시작',
  'lobby.startHint': '둘 이상 모여 전원 준비되면 시작할 수 있다',
  'lobby.startWait': '방장이 시작한다',
  /* 방장 설정. '동사'·'예산'은 코드의 말이다(GLOSSARY 2절) - 화면은 '이상한 곳의 종류'와 '수'로 말한다. */
  'lobby.settings': '방장 설정',
  'lobby.settings.verbs': '이상한 곳의 종류',
  /** **인당** 이상한 곳 수(M11). 라운드 총량은 인원 × 이 값이라 손잡이는 사람 몫이다. */
  'lobby.settings.count': '한 사람당 이상한 곳',
  'lobby.settings.less': '하나 줄이기',
  'lobby.settings.more': '하나 늘리기',
  /*
   * 꾸미기 토글(M10). 이름은 페이즈와 같은 말이어야 한다(`phase.decorate.name` - '꾸미기').
   * 칩 글자는 '켠다/끈다'가 아니라 **켰을 때의 뜻**이다 - 눌린 상태를 `aria-pressed`가 말하므로
   * 글자까지 뒤집으면 두 곳이 같은 것을 두 번, 서로 반대로 말한다(동사 칩과 같은 규약).
   */
  'lobby.settings.decorate': '꾸미기',
  'lobby.settings.decorate.on': '빈 방부터 함께 꾸민다',

  /*
   * --- 연결 (로드맵 M6 - 끊김 · 재접속 · 서버의 거절) ----------------------------------
   *
   * 배너(`ui/connection.ts`)와 로비 폼의 오류 줄이 같은 키를 쓴다. 서버의 `error.code`(`net/protocol.ts`의 `ErrorCode`) 값 그대로
   * `net.error.<code>`다 - 코드가 하나 늘면 컴파일이 아니라 `[[key]]`가 먼저 알려 주므로 `index.test.ts`가 목록을 대조한다.
   */
  'net.reconnecting': '연결이 끊겼다. 다시 잇는 중',
  'net.resumed': '자리로 돌아왔다',
  'net.closed': '연결이 끊겼다',
  'net.error.version': '판이 새 버전이다. 새로고침해라',
  'net.error.no-room': '그 번호의 방이 없다',
  'net.error.full': '방이 가득 찼다',
  'net.error.in-progress': '이미 시작한 방이다',
  'net.error.superseded': '다른 창에서 같은 자리에 들어왔다',
  /* 서버가 말한 것이 아니라 소켓이 한 번도 열리지 못한 경우(`ui/lobby.LobbyFormError`). 배너에는 뜨지 않고 폼에만 뜬다. */
  'net.error.offline': '방 서버에 닿지 못했다. 잠시 뒤 다시 해 봐라',

  /* --- 오류 ---------------------------------------------------------------- */

  'error.bootFailed': '게임을 시작하지 못했다',
  'error.bootDetail': '새로고침해도 같으면 방 번호를 적어 두고 알려 달라',
  'error.saveFailed': '진행을 저장하지 못했다. 이번만 다음 단계로 넘어간다',
  'error.assetsFallback': '모델을 다 읽지 못해 임시 모양으로 대신한다',
} as const

/** 사전이 가진 키 전부. `en.ts`가 이 목록을 그대로 채워야 컴파일이 통과한다. */
export type MessageKey = keyof typeof ko

/** 한 언어의 사전. 값은 전부 문자열이며, 치환은 `index.ts`가 한다. */
export type Messages = Readonly<Record<MessageKey, string>>
